import { postRpcCreateUser } from "./generated/calls/rpc-create-user/rpc-create-user";
import { getRpcCurrentUserId } from "./generated/calls/rpc-current-user-id/rpc-current-user-id";
import {
  getUserSettings,
  patchUserSettings,
  postUserSettings,
} from "./generated/calls/user-settings/user-settings";
import { getUsers, patchUsers } from "./generated/calls/users/users";
import type {
  Users as UserDTO,
  UserSettings as UserSettingsDTO,
  UserSettingsInsert as UserSettingsInsertDTO,
  UserSettingsUpdate as UserSettingsUpdateDTO,
  UsersUpdate as UserUpdateDTO,
} from "./generated/models";
import { buildHeaders, headersToObject } from "./helpers/headerHelper";
import { postgrestError } from "./helpers/postgrestErrorHelper";
import { postgrestFilter } from "./helpers/queryHelper";
import type { APIResult, APIResultNoContent } from "./types";

export type { UserDTO, UserSettingsDTO, UserSettingsInsertDTO, UserSettingsUpdateDTO };

export type UserExpandedDTO = UserDTO & {
  current_user: boolean;
};

// ---------------------------------------------------------------------------- //
// ---------------------------------- Users ----------------------------------- //
// ---------------------------------------------------------------------------- //

/**
 * Resolves the database ID of the currently authenticated user.
 *
 * @returns The current user's database ID when one exists.
 */
async function fetchCurrentUsersId() {
  const headers = buildHeaders();
  return await getRpcCurrentUserId({ headers });
}

/**
 * Creates the application user associated with the current authentication token.
 *
 * Used when no existing application user can be resolved for the authenticated
 * identity.
 *
 * @returns The newly created user when creation succeeds.
 */
async function createUserFromToken() {
  const headers = buildHeaders();
  return await postRpcCreateUser({ headers });
}

/**
 * Fetches the complete application user collection.
 *
 * This is intentionally kept separate from resolving the current user so the
 * API boundary can evolve independently if a dedicated current-user endpoint
 * is introduced later.
 *
 * @returns The available application users.
 */
async function fetchUsers() {
  const headers = buildHeaders();
  return await getUsers(undefined, { headers });
}

/**
 * Resolves the authenticated application user and fetches the available users.
 *
 * The current backend does not expose a dedicated "get current user" endpoint,
 * so the current user's ID is resolved first and a user is created from the
 * authentication token when no existing user is found. The users collection is
 * then fetched and enriched with a `current_user` flag.
 *
 * The complete user collection is intentionally returned rather than only the
 * current user, as the application also requires access to other users.
 *
 * @returns The available users with the current user identified, or an error
 *          describing why the user context could not be established.
 */
export async function resolveAndFetchUsers(): Promise<
  APIResult<UserExpandedDTO[]>
> {
  try {
    let currentUserId: string;

    // Resolve current user
    try {
      const currentUser = await fetchCurrentUsersId();

      if (currentUser.status !== 200) {
        throw postgrestError("Resolving the current user failed", currentUser);
      }

      if (currentUser.data) {
        currentUserId = currentUser.data;
      } else {
        const created = await createUserFromToken();

        if (created.status !== 200) {
          throw postgrestError("Creating the current user failed", created);
        }

        // `create_user()` is declared `RETURNS void`, so the new user's ID has
        // to be resolved with a second call once the row exists.
        const createdUser = await fetchCurrentUsersId();

        if (createdUser.status !== 200 || !createdUser.data) {
          return {
            success: false,
            errorMsg: "Unable to create current user",
          };
        }

        currentUserId = createdUser.data;
      }
    } catch (error) {
      return {
        success: false,
        errorMsg:
          error instanceof Error
            ? error.message
            : "Unable to resolve current user",
      };
    }

    // Fetch users
    let users: UserDTO[];

    try {
      const result = await fetchUsers();

      if (result.status !== 200 && result.status !== 206) {
        throw postgrestError("Fetch failed", result);
      }

      users = result.data;
    } catch (error) {
      return {
        success: false,
        errorMsg:
          error instanceof Error ? error.message : "Unable to fetch users",
      };
    }

    // Enrich
    const expandedUsers = users.map((user) => ({
      ...user,
      current_user: user.id === currentUserId,
    }));

    return {
      success: true,
      data: expandedUsers,
    };
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error
          ? error.message
          : "an unknown error has occurred",
    };
  }
}

export type UpdateUserDTO = UserUpdateDTO;

export async function updateUser(
  userId: string,
  data: UpdateUserDTO,
): Promise<APIResultNoContent> {
  try {
    const headers = buildHeaders();
    const response = await patchUsers(data, { id: userId }, { headers });

    if (response.status !== 200) {
      return {
        success: false,
        errorMsg: `Failed to update user. API returned status ${response.status}.`,
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while updating the user.",
    };
  }
}

// ---------------------------------------------------------------------------- //
// ------------------------------ User settings ------------------------------- //
// ---------------------------------------------------------------------------- //

/**
 * Fetches all fleet users settings from the API.
 *
 * Returns an API result containing the fetched user settings, or a controlled
 * failure if the request could not be completed.
 */
export async function fetchUsersPrefs(): Promise<APIResult<UserSettingsDTO[]>> {
  try {
    const headers = buildHeaders();
    const settingsFetchRes = await getUserSettings(undefined, { headers });

    switch (settingsFetchRes.status) {
      // A read narrowed by `limit`, `offset` or a `Range` header answers 206
      // and carries the same rows as a 200.
      case 200:
      case 206:
        return {
          success: true,
          data: settingsFetchRes.data,
        };
      default:
        throw postgrestError("Fetch failed", settingsFetchRes);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

/**
 * Creates a new user settings row for a user with no existing preferences.
 */
export async function createUserPrefs(
  data: UserSettingsInsertDTO,
): Promise<APIResult<UserSettingsDTO>> {
  try {
    const headers = buildHeaders();
    const createRes = await postUserSettings(data, undefined, {
      headers: headersToObject(headers),
    });

    switch (createRes.status) {
      case 201: {
        // An insert returns the rows it created, so a single insert returns
        // a one-element array.
        const created = createRes.data[0];

        if (!created) {
          throw new Error("Create succeeded but returned no user settings");
        }

        return {
          success: true,
          data: created,
        };
      }

      default:
        throw postgrestError("Create failed", createRes);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

/**
 * Updates an existing user settings row.
 */
export async function updateUserPrefs(
  userId: string,
  data: UserSettingsUpdateDTO,
): Promise<APIResultNoContent> {
  try {
    const headers = buildHeaders();
    const updateRes = await patchUserSettings(
      data,
      { user_id: postgrestFilter(userId) },
      { headers: headersToObject(headers) },
    );

    switch (updateRes.status) {
      case 200:
        return {
          success: true,
        };

      default:
        throw postgrestError("Update failed", updateRes);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}
